import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel as InputLabelMUI,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { DigitalClock, LocalizationProvider, StaticDatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import cn from 'classnames';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { timezones } from '../../../../assets/data/timezones';
import { getDefaultExpirationDate } from '../../../../constants/bucket';
import { DateFormatsEnum, DEFAULT_TIMEZONE } from '../../../../constants/date';
import { ITimezone } from '../../../../types/timezone';
import {
  detectUserTimezone,
  findTimezoneByText,
  getMinTimeForSelectedDate,
  getRoundedMinDate,
  getTimezoneDisplayText,
  getTimezoneLabel,
  getWallClock,
  resolveWallClockInstant,
  timeOfDay,
  TIME_REFERENCE_DATE,
  wallClockDay,
  wallClockOf,
} from '../../../../utils/date';
import { InputLabel } from '../../labels';

interface IDateAndTimezonePickerProps {
  className?: string;
  value?: string | null;
  onChange?: (value: { selectedDate: Date | null } | null) => void;
  minDate?: Date;
  maxDate?: Date;
  hint?: React.ReactNode;
  isLoading?: boolean;
  readOnly?: boolean;
  onSubmit?: (
    value: { selectedDate: Date | null; selectedDateString: string | null; timezone: ITimezone } | null,
  ) => void;
  defaultValue?: string | null;
  showLabel?: boolean;
  defaultView?: boolean;
  resetLabel?: string;
  label?: string;
  onReset?: () => void;
  isClearable?: boolean;
  timezone?: ITimezone | null;
}

const TIME_PICKER_VIEWS = ['hours'] as const;
const minutesOfDay = (date: Date): number => date.getHours() * 60 + date.getMinutes();

export const DateAndTimezonePicker: React.FC<IDateAndTimezonePickerProps> = ({
  className,
  value,
  onChange,
  minDate,
  maxDate,
  hint,
  isLoading = false,
  readOnly = false,
  defaultValue = null,
  onSubmit,
  showLabel = true,
  defaultView = false,
  resetLabel = 'Set to default',
  onReset,
  label = 'Select expiration',
  isClearable = false,
  timezone: storedTimezone,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [timezone, setTimezone] = useState<ITimezone>(() => storedTimezone ?? detectUserTimezone());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);

  const [dateToDisplay, setDateToDisplay] = useState<Date | null>(null);
  const [timeToDisplay, setTimeToDisplay] = useState<Date | null>(null);
  const [timezoneToDisplay, setTimezoneToDisplay] = useState<ITimezone>(() => storedTimezone ?? detectUserTimezone());

  const hasValue = useMemo(() => selectedDate || time, [selectedDate, time]);

  const handleClear = useCallback(() => {
    if (readOnly) {
      return;
    }

    setSelectedDate(null);
    setTime(null);
    setDateToDisplay(null);
    setTimeToDisplay(null);
  }, [selectedDate, time, readOnly]);

  const storedIso = value || defaultValue || null;

  const triggerOnChange = useCallback(
    (newDate: Date | null, newTime: Date | null) => {
      if (readOnly) {
        return;
      }
      if (newDate && newTime) {
        onChange?.({ selectedDate: resolveWallClockInstant(wallClockOf(newDate, newTime), timezone, storedIso) });
      }
    },
    [onChange, readOnly, timezone, storedIso],
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const submit = useCallback(() => {
    if (selectedDate && time) {
      setTimezoneToDisplay(timezone);
      const utcDate = resolveWallClockInstant(wallClockOf(selectedDate, time), timezone, storedIso);
      onSubmit?.({ selectedDate: utcDate, selectedDateString: utcDate.toISOString(), timezone });
    } else {
      onSubmit?.(null);
    }
  }, [onSubmit, selectedDate, time, timezone, storedIso]);

  const handleClose = useCallback(() => {
    setTimezone(timezoneToDisplay);
    setSelectedDate(dateToDisplay);
    setTime(timeToDisplay);
    setAnchorEl(null);
  }, [timezoneToDisplay, dateToDisplay, timeToDisplay]);

  const handleSubmit = useCallback(() => {
    submit();
    setAnchorEl(null);
  }, [submit]);

  const handleTimezoneChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (readOnly) {
        return;
      }
      const newTz = findTimezoneByText(event.target.value) ?? DEFAULT_TIMEZONE;
      setTimezone(newTz);
    },
    [readOnly],
  );

  const handleTimeChange = useCallback(
    (newTime: Date | null) => {
      if (readOnly) {
        return;
      }
      if (newTime) {
        const nextTime = timeOfDay(newTime.getHours());
        setTime(nextTime);
        triggerOnChange(selectedDate, nextTime);
      }
    },
    [readOnly, selectedDate, triggerOnChange],
  );

  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      if (readOnly) {
        return;
      }
      setSelectedDate(newDate);
      triggerOnChange(newDate, time);
    },
    [readOnly, time, triggerOnChange],
  );

  const handleReset = useCallback(() => {
    if (readOnly) {
      return;
    }
    const defaultExpirationDate = getDefaultExpirationDate();
    setSelectedDate(defaultExpirationDate);

    setTime(timeOfDay(23, 59, 59));
    setTimezone(detectUserTimezone());
    onReset?.();
  }, [onReset, readOnly]);

  const labelInstant = useMemo(
    () =>
      selectedDate && time ? resolveWallClockInstant(wallClockOf(selectedDate, time), timezone, storedIso) : new Date(),
    [selectedDate, time, timezone, storedIso],
  );

  const timezoneOptions = useMemo(
    () => timezones.map((tz) => ({ tz, label: getTimezoneDisplayText(tz, labelInstant) })),
    [labelInstant],
  );

  const formattedDateTime = useMemo(() => {
    if (!dateToDisplay || !timeToDisplay) {
      return label;
    }

    const instant = resolveWallClockInstant(wallClockOf(dateToDisplay, timeToDisplay), timezoneToDisplay, storedIso);

    return `${format(dateToDisplay, DateFormatsEnum.MMMdyyyy)}, ${format(
      timeToDisplay,
      DateFormatsEnum.HHmm,
    )} ${getTimezoneLabel(timezoneToDisplay, instant)}`;
  }, [dateToDisplay, timeToDisplay, timezoneToDisplay, label, storedIso]);

  const isTimeValid = useMemo(() => {
    // one-link mode
    if (isClearable) {
      if (!selectedDate && !time) {
        return true;
      }
    }

    // other modes
    if (!selectedDate || !time) {
      return false;
    }

    const minTime = getMinTimeForSelectedDate(selectedDate, timezone);
    if (!minTime) {
      return true;
    }

    return minutesOfDay(time) >= minutesOfDay(minTime);
  }, [isClearable, selectedDate, time, timezone]);

  const renderResetButton = useCallback(() => {
    if (isClearable) {
      return (
        hasValue && (
          <Button variant="text" onClick={handleClear} sx={{ mr: 'auto' }}>
            Clear
          </Button>
        )
      );
    }

    return (
      <Button variant="text" onClick={handleReset} sx={{ mr: 'auto' }}>
        {resetLabel}
      </Button>
    );
  }, [isClearable, handleClear, handleReset, hasValue, resetLabel]);

  useEffect(() => {
    const dateStr = value || defaultValue;
    const zone = storedTimezone === undefined ? timezone : storedTimezone ?? detectUserTimezone();

    if (storedTimezone !== undefined) {
      setTimezone(zone);
      setTimezoneToDisplay(zone);
    }

    if (dateStr) {
      const wallClock = getWallClock(dateStr, zone);
      const day = wallClockDay(wallClock);
      const clock = timeOfDay(wallClock.hours, wallClock.minutes, wallClock.seconds);

      setSelectedDate(day);
      setDateToDisplay(day);
      setTime(clock);
      setTimeToDisplay(clock);
    } else {
      setSelectedDate(null);
      setTime(null);

      setTimeToDisplay(null);
      setDateToDisplay(null);
    }
  }, [value, defaultValue, storedTimezone?.text]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className={cn(className)}>
        {showLabel && <InputLabel value="Expiration date" />}
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box>
            <Button
              id="demo-positioned-button"
              onClick={handleClick}
              sx={{
                pl: 0,
                whiteSpace: 'nowrap',
                fontSize: '16px',
                fontWeight: 500,
                paddingLeft: 1,
                paddingRight: 0.25,
              }}
            >
              {formattedDateTime}
              <ArrowDropDownIcon />
            </Button>
            <Menu
              id="demo-positioned-menu"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleClose}
            >
              <Box sx={{ width: 320, paddingTop: '15px', mx: 'auto' }}>
                <FormControl fullWidth sx={{ px: 2 }}>
                  <InputLabelMUI sx={{ pl: 2 }} id="timezone-label">
                    Timezone
                  </InputLabelMUI>
                  <Select
                    labelId="timezone-label"
                    value={timezone.text}
                    label="Timezone"
                    onChange={handleTimezoneChange}
                    disabled={readOnly}
                    fullWidth
                  >
                    {timezoneOptions.map(({ tz, label: tzLabel }) => (
                      <MenuItem key={tz.text} value={tz.text}>
                        {tzLabel}
                      </MenuItem>
                    ))}
                  </Select>

                  <TimePicker
                    viewRenderers={{
                      hours: (props) => {
                        return (
                          <DigitalClock
                            ampm={false}
                            timeStep={60}
                            value={time}
                            referenceDate={TIME_REFERENCE_DATE}
                            minTime={getMinTimeForSelectedDate(selectedDate, timezone)}
                            onChange={handleTimeChange}
                            skipDisabled={true}
                            slots={{
                              digitalClockItem: (itemProps) => {
                                const hourValue = itemProps.children.split(':')[0];
                                const formattedHour = `${hourValue?.toString().padStart(2, '0')}:00`;

                                return (
                                  <MenuItem
                                    {...itemProps}
                                    selected={itemProps.selected}
                                    onClick={() => itemProps.onClick?.(itemProps.value)}
                                  >
                                    {formattedHour}
                                  </MenuItem>
                                );
                              },
                            }}
                          />
                        );
                      },
                    }}
                    ampm={false}
                    value={time}
                    referenceDate={TIME_REFERENCE_DATE}
                    onChange={handleTimeChange}
                    sx={{ width: '100%', mt: 2 }}
                    format="HH:00"
                    views={TIME_PICKER_VIEWS}
                    disabled={readOnly}
                    minTime={getMinTimeForSelectedDate(selectedDate, timezone)}
                    slotProps={{
                      textField: {
                        inputProps: {
                          readOnly: true,
                        },
                      },
                    }}
                  />
                </FormControl>

                <Box sx={{ mt: 2 }}>
                  <StaticDatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    minDate={minDate || getRoundedMinDate(timezone)}
                    maxDate={maxDate}
                    readOnly={readOnly}
                    referenceDate={defaultView && selectedDate ? selectedDate : undefined}
                    slotProps={{
                      layout: { sx: { width: '100%' } },
                    }}
                    slots={{
                      toolbar: () => null,
                      actionBar: () => null,
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 2 }}>
                  {renderResetButton()}
                  <Button variant="contained" onClick={handleSubmit} disabled={!isTimeValid || readOnly}>
                    OK
                  </Button>
                </Box>
              </Box>
            </Menu>
          </Box>
        )}
        {hint && <Typography variant="caption">{hint}</Typography>}
      </Box>
    </LocalizationProvider>
  );
};
